import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import ShareButtons from './ShareButtons';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-r from-gray-900 to-slate-800 text-white py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <img src="/favicon.svg" alt="$WYMR? logo" className="w-10 h-10 rounded-lg" />
              <div>
                <span className="font-heading font-bold text-xl block leading-tight">$WYMR?</span>
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">Who You Makin' Rich</span>
              </div>
            </div>
            <p className="text-gray-400 mb-5 leading-relaxed text-sm">
              Providing transparency in corporate ownership and business relationships worldwide.
            </p>
            <div className="mb-4">
              <span className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Share this site</span>
              <ShareButtons
                compact={false}
                title="Who You Makin' Rich — Corporate Transparency Platform"
                text="Discover who owns what, who donates where, and who's really making money. Explore financial connections of billionaires, celebrities, athletes & politicians."
                url={typeof window !== 'undefined' ? window.location.origin : ''}
              />
            </div>
          </div>

          {/* Explore */}
          <div>
            <h3 className="font-bold text-sm text-white mb-5">Explore</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><Link to="/search-results?tab=companies" className="hover:text-white transition-colors">Companies</Link></li>
              <li><Link to="/search-results?tab=people" className="hover:text-white transition-colors">Executives</Link></li>
              <li><Link to="/search-results?tab=people&q=investor" className="hover:text-white transition-colors">Investors</Link></li>
              <li><Link to="/pac-directory" className="hover:text-white transition-colors">PACs & Politics</Link></li>
              <li><Link to="/shareholders" className="hover:text-white transition-colors">Shareholders</Link></li>
            </ul>
          </div>

          {/* Features */}
          <div>
            <h3 className="font-bold text-sm text-white mb-5">Features</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><Link to="/search-results" className="hover:text-white transition-colors">Advanced Search</Link></li>
              <li><Link to="/shareholders" className="hover:text-white transition-colors">Network Visualization</Link></li>
              <li><Link to="/pac-directory" className="hover:text-white transition-colors">PAC Directory</Link></li>
              <li><Link to="/" className="hover:text-white transition-colors">Prominent Figures</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li>
                <Link to="/donate" className="hover:text-emerald-400 transition-colors flex items-center gap-1.5">
                  <Icon name="Heart" size={13} className="text-red-400" />
                  Donate
                </Link>
              </li>
              <li>
                <a href="https://merch.whoareyoumakingrich.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors flex items-center gap-1.5">
                  <Icon name="ShoppingBag" size={13} className="text-amber-400" />
                  Merch Store
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & About */}
          <div>
            <h3 className="font-bold text-sm text-white mb-5">Contact</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <a href="mailto:info@whoyoumakinrich.com" className="hover:text-cyan-400 transition-colors flex items-center gap-2">
                  <Icon name="Mail" size={14} className="text-cyan-400" />
                  info@whoyoumakinrich.com
                </a>
              </li>
              <li>
                <a href="mailto:support@whoyoumakinrich.com" className="hover:text-cyan-400 transition-colors flex items-center gap-2">
                  <Icon name="HelpCircle" size={14} className="text-emerald-400" />
                  support@whoyoumakinrich.com
                </a>
              </li>
              <li>
                <a href="mailto:bugs@whoyoumakingrich.com" className="hover:text-orange-400 transition-colors flex items-center gap-2">
                  <Icon name="MessageSquare" size={14} className="text-orange-400" />
                  <span>Feedback: Found something wrong or is info missing? Let us know!</span>
                </a>
              </li>
            </ul>
            <div className="mt-5">
              <h3 className="font-bold text-sm text-white mb-3">Data Sources</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><span className="cursor-default">Wikipedia, SEC, FEC</span></li>
                <li><span className="cursor-default">Updated with AI daily</span></li>
                <li><span className="cursor-default">100% free to use</span></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-12 pt-10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Left: Copyright + DayDream & Do */}
            <div className="text-center sm:text-left">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Who You Makin' Rich? All rights reserved.
              </p>
              <p className="text-xs text-gray-600 mt-2 flex flex-wrap gap-x-4 gap-y-1 justify-center sm:justify-start">
                <Link to="/privacy_policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              </p>
              <p className="text-xs text-gray-600 mt-1">
                A product of{' '}
                <a
                  href="https://daydreamanddo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 font-semibold hover:text-white transition-colors"
                >
                  DayDream &amp; Do™
                </a>
              </p>
            </div>

            {/* Right: Trust badges */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Icon name="Shield" size={14} className="text-green-400" />
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Icon name="Database" size={14} className="text-yellow-400" />
                <span>Verified Data</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Icon name="Cpu" size={14} className="text-cyan-400" />
                <span>AI-Powered</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
